import type {
  CompleteRoundAction,
  GameOutcome,
  GameState,
  LockVoteAction,
  ResolveVoteAction,
  RevealCardAction,
  RoleId,
  SetDiscussionDurationAction,
  StartNextRoundAction,
  StartVoteAction,
  TickDiscussionAction,
  TickVoteAction,
  VoteCount,
  VoteLine,
  VoteResult,
} from "../types";
import { ROLE_DEFINITIONS } from "../types";

function touch(state: GameState): GameState {
  return { ...state, version: state.version + 1 };
}

function currentCardIdAtPlayer(
  state: GameState,
  playerId: string,
): string | null {
  const entry = Object.entries(state.cardPositions).find(
    ([, position]) =>
      position.zone === "player" && position.playerId === playerId,
  );
  return entry?.[0] ?? null;
}

// The doppelganger card counts as its copied role for every end-of-game
// judgement, regardless of who ends up holding it.
function effectiveRoleId(state: GameState, roleId: RoleId): RoleId {
  if (roleId !== "doppelganger") return roleId;
  return state.doppelgangerCopiedRoleId ?? roleId;
}

function currentRoleIdAtPlayer(
  state: GameState,
  playerId: string,
): RoleId | null {
  const cardId = currentCardIdAtPlayer(state, playerId);
  if (!cardId) return null;
  const roleId = state.cards.find((card) => card.id === cardId)?.roleId ?? null;
  return roleId === null ? null : effectiveRoleId(state, roleId);
}

function cardIdAtRevealTarget(
  state: GameState,
  target: RevealCardAction["target"],
): string | null {
  if (target.kind === "center") {
    return state.centerSlots[target.slotIndex] ?? null;
  }
  return currentCardIdAtPlayer(state, target.playerId);
}

function timedOutStateWithAutoVotes(state: GameState): GameState {
  if (state.voteRemainingSeconds !== 0) return state;

  const autoVotePlayerIds = state.players
    .filter((player) => !player.voteTargetId)
    .map((player) => player.id);

  if (autoVotePlayerIds.length === 0)
    return { ...state, autoVotePlayerIds: [] };

  return {
    ...state,
    autoVotePlayerIds,
    players: state.players.map((player) => {
      if (player.voteTargetId) return player;
      return { ...player, voteTargetId: player.id };
    }),
  };
}

function buildVoteLines(state: GameState): readonly VoteLine[] {
  return state.players.flatMap((player) => {
    if (!player.voteTargetId) return [];
    return [
      {
        voterPlayerId: player.id,
        targetPlayerId: player.voteTargetId,
      },
    ];
  });
}

function buildVoteCounts(
  state: GameState,
  voteLines: readonly VoteLine[],
): readonly VoteCount[] {
  const countsByPlayerId = new Map<string, number>(
    state.players.map((player) => [player.id, 0]),
  );

  for (const voteLine of voteLines) {
    const previous = countsByPlayerId.get(voteLine.targetPlayerId) ?? 0;
    countsByPlayerId.set(voteLine.targetPlayerId, previous + 1);
  }

  const maxCount = Math.max(0, ...countsByPlayerId.values());

  return state.players.map((player) => {
    const count = countsByPlayerId.get(player.id) ?? 0;
    return {
      playerId: player.id,
      count,
      isTop: maxCount > 0 && count === maxCount,
    };
  });
}

function findBaseEliminatedPlayerIds(
  voteCounts: readonly VoteCount[],
): readonly string[] {
  const maxCount = Math.max(
    0,
    ...voteCounts.map((voteCount) => voteCount.count),
  );
  if (maxCount < 2) return [];
  return voteCounts
    .filter((voteCount) => voteCount.count === maxCount)
    .map((voteCount) => voteCount.playerId);
}

function includeHunterTargets(
  state: GameState,
  eliminatedPlayerIds: readonly string[],
): readonly string[] {
  const eliminatedSet = new Set(eliminatedPlayerIds);

  for (const player of state.players) {
    if (!eliminatedSet.has(player.id)) continue;
    if (currentRoleIdAtPlayer(state, player.id) !== "hunter") continue;
    if (player.voteTargetId) {
      eliminatedSet.add(player.voteTargetId);
    }
  }

  return state.players
    .filter((player) => eliminatedSet.has(player.id))
    .map((player) => player.id);
}

function buildVoteResult(state: GameState): VoteResult {
  const voteLines = buildVoteLines(state);
  const voteCounts = buildVoteCounts(state, voteLines);
  const baseEliminatedPlayerIds = findBaseEliminatedPlayerIds(voteCounts);
  const eliminatedPlayerIds = includeHunterTargets(
    state,
    baseEliminatedPlayerIds,
  );

  return {
    voteLines,
    voteCounts,
    eliminatedPlayerIds,
  };
}

function hasCurrentRole(
  state: GameState,
  playerIds: readonly string[],
  roleId: RoleId,
): boolean {
  return playerIds.some(
    (playerId) => currentRoleIdAtPlayer(state, playerId) === roleId,
  );
}

function hasAnyCurrentWerewolf(state: GameState): boolean {
  return state.players.some(
    (player) => currentRoleIdAtPlayer(state, player.id) === "werewolf",
  );
}

function buildGameOutcome(
  state: GameState,
  voteResult: VoteResult,
): GameOutcome {
  const eliminatedPlayerIds = voteResult.eliminatedPlayerIds;
  const isTannerEliminated = hasCurrentRole(
    state,
    eliminatedPlayerIds,
    "tanner",
  );
  const isWerewolfEliminated = hasCurrentRole(
    state,
    eliminatedPlayerIds,
    "werewolf",
  );

  if (isTannerEliminated && isWerewolfEliminated) {
    return {
      winningTeam: ROLE_DEFINITIONS.tanner.team,
      winningTeams: [
        ROLE_DEFINITIONS.tanner.team,
        ROLE_DEFINITIONS.villager.team,
      ],
      reason: "tanner-and-werewolf-eliminated",
      eliminatedPlayerIds,
    };
  }

  if (isTannerEliminated) {
    return {
      winningTeam: ROLE_DEFINITIONS.tanner.team,
      winningTeams: [ROLE_DEFINITIONS.tanner.team],
      reason: "tanner-eliminated",
      eliminatedPlayerIds,
    };
  }

  if (isWerewolfEliminated) {
    return {
      winningTeam: ROLE_DEFINITIONS.villager.team,
      winningTeams: [ROLE_DEFINITIONS.villager.team],
      reason: "werewolf-eliminated",
      eliminatedPlayerIds,
    };
  }

  // No werewolf was eliminated. A surviving werewolf in play wins for the
  // werewolf team.
  if (hasAnyCurrentWerewolf(state)) {
    return {
      winningTeam: ROLE_DEFINITIONS.werewolf.team,
      winningTeams: [ROLE_DEFINITIONS.werewolf.team],
      reason: "werewolf-survived",
      eliminatedPlayerIds,
    };
  }

  // No werewolf is in play (all werewolf cards are in the center, or none were
  // dealt to a player). The village wins only if nobody died.
  if (eliminatedPlayerIds.length === 0) {
    return {
      winningTeam: ROLE_DEFINITIONS.villager.team,
      winningTeams: [ROLE_DEFINITIONS.villager.team],
      reason: "no-werewolves-no-deaths",
      eliminatedPlayerIds,
    };
  }

  // No werewolf in play but a player died. A surviving minion (werewolf team)
  // wins in the werewolf team's place.
  const minionSurvived = state.players.some(
    (player) =>
      currentRoleIdAtPlayer(state, player.id) === "minion" &&
      !eliminatedPlayerIds.includes(player.id),
  );
  if (minionSurvived) {
    return {
      winningTeam: ROLE_DEFINITIONS.minion.team,
      winningTeams: [ROLE_DEFINITIONS.minion.team],
      reason: "minion-survived",
      eliminatedPlayerIds,
    };
  }

  // No werewolf and no surviving werewolf-team member, yet a player died — the
  // village loses and nobody wins (winningTeams is intentionally empty). The
  // required winningTeam field is unused by consumers; winningTeams is the
  // authoritative winner set.
  return {
    winningTeam: ROLE_DEFINITIONS.villager.team,
    winningTeams: [],
    reason: "no-werewolves-village-loss",
    eliminatedPlayerIds,
  };
}

export function applyStartVote(
  state: GameState,
  _action: StartVoteAction,
): GameState {
  return touch({
    ...state,
    phase: "vote",
    discussionRemainingSeconds: 0,
    voteRemainingSeconds: state.voteWindowSeconds,
    autoVotePlayerIds: [],
  });
}

export function applySetDiscussionDuration(
  state: GameState,
  action: SetDiscussionDurationAction,
): GameState {
  return touch({
    ...state,
    discussionDurationSeconds: action.durationSeconds,
    discussionRemainingSeconds: null,
  });
}

export function applyTickDiscussion(
  state: GameState,
  action: TickDiscussionAction,
): GameState {
  const currentRemaining =
    state.discussionRemainingSeconds ?? state.discussionDurationSeconds;
  const discussionRemainingSeconds = Math.max(
    0,
    currentRemaining - action.elapsedSeconds,
  );

  if (discussionRemainingSeconds === 0) {
    return touch({
      ...state,
      phase: "vote",
      discussionRemainingSeconds,
      voteRemainingSeconds: state.voteWindowSeconds,
      autoVotePlayerIds: [],
    });
  }

  return touch({
    ...state,
    phase: "day",
    discussionRemainingSeconds,
    voteRemainingSeconds: null,
  });
}

export function applyLockVote(
  state: GameState,
  action: LockVoteAction,
): GameState {
  return touch({
    ...state,
    phase: "vote",
    players: state.players.map((player) => {
      if (player.id !== action.actorPlayerId) return player;
      return { ...player, voteTargetId: action.targetPlayerId };
    }),
  });
}

export function applyTickVote(
  state: GameState,
  action: TickVoteAction,
): GameState {
  const currentRemaining =
    state.voteRemainingSeconds ?? state.voteWindowSeconds;
  return touch({
    ...state,
    phase: "vote",
    voteRemainingSeconds: Math.max(0, currentRemaining - action.elapsedSeconds),
  });
}

export function applyResolveVote(
  state: GameState,
  _action: ResolveVoteAction,
): GameState {
  const completedState = timedOutStateWithAutoVotes(state);
  const voteResult = buildVoteResult(completedState);
  const gameOutcome = buildGameOutcome(completedState, voteResult);

  return touch({
    ...completedState,
    phase: "reveal",
    voteResult,
    gameOutcome,
  });
}

export function applyRevealCard(
  state: GameState,
  action: RevealCardAction,
): GameState {
  const cardId = cardIdAtRevealTarget(state, action.target);
  if (!cardId || state.revealedCardIds.includes(cardId)) {
    return state;
  }

  return touch({
    ...state,
    phase: state.phase === "complete" ? "complete" : "reveal",
    revealedCardIds: [...state.revealedCardIds, cardId],
  });
}

export function applyCompleteRound(
  state: GameState,
  _action: CompleteRoundAction,
): GameState {
  return touch({ ...state, phase: "complete" });
}

export function applyStartNextRound(
  state: GameState,
  _action: StartNextRoundAction,
): GameState {
  return touch({
    ...state,
    seed: `${state.seed}:next-round:${state.version + 1}`,
    phase: "setup",
    players: state.players.map((player) => ({
      ...player,
      startingCardId: null,
      voteTargetId: null,
    })),
    seatLocked: false,
    cards: [],
    cardPositions: {},
    centerSlots: [null, null, null],
    privateKnowledge: {},
    doppelgangerCopiedRoleId: null,
    rolePeekConfirmedPlayerIds: [],
    nightRoleQueue: [],
    announcerTimeline: [],
    nightLog: [],
    discussionRemainingSeconds: null,
    voteRemainingSeconds: null,
    autoVotePlayerIds: [],
    voteResult: null,
    revealedCardIds: [],
    gameOutcome: null,
  });
}
